/**
 * SavingsPanel — displays estimated time & money saved by using GawronifyOS.
 * Fetched from /api/user/savings endpoint.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiRequest } from "@/lib/queryClient";
import { Clock, Banknote, MessageSquare, Zap, TrendingUp, Loader2 } from "lucide-react";

interface ModuleBreakdown {
  moduleId: string;
  label: string;
  conversationCount: number;
  messageCount: number;
  tokensUsed: number;
  hoursSaved: number;
  moneySaved: number;
}

interface SavingsData {
  totalHoursSaved: number;
  totalMoneySaved: number;
  totalConversations: number;
  totalMessages: number;
  totalTokensUsed: number;
  hourlyRate: number;
  modules: ModuleBreakdown[];
  memberSince: string | null;
}

function StatCard({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-5 flex flex-col gap-2">
      <div className="flex items-center gap-2 text-white/40">
        {icon}
        <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-heading-2 font-extrabold text-white">{value}</p>
      {sub && <p className="text-xs text-white/30">{sub}</p>}
    </div>
  );
}

function ModuleRow({ mod }: { mod: ModuleBreakdown }) {
  const { t, i18n } = useTranslation();
  const numLocale = i18n.language === "pl" ? "pl-PL" : "en-US";
  return (
    <div className="flex items-center gap-4 py-3 border-b border-white/[0.04] last:border-b-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">{mod.label}</p>
        <p className="text-xs text-white/30">
          {t("savings.conversations", { count: mod.conversationCount })} ·{" "}
          {t("savings.messages", { count: mod.messageCount })}
        </p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-sm font-semibold text-emerald-400">~{mod.hoursSaved.toFixed(1)}h</p>
        <p className="text-xs text-white/30">
          ~{mod.moneySaved.toLocaleString(numLocale)} {t("savings.currency")}
        </p>
      </div>
    </div>
  );
}

export default function SavingsPanel() {
  const { t, i18n } = useTranslation();
  const numLocale = i18n.language === "pl" ? "pl-PL" : "en-US";
  const { data, isLoading, error } = useQuery<SavingsData>({
    queryKey: ["user-savings"],
    queryFn: () => apiRequest("GET", "/api/user/savings"),
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-white/20 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-6">
        <p className="text-white/40 text-sm">{t("savings.loadError")}</p>
      </div>
    );
  }

  const memberDays = data.memberSince
    ? Math.max(
        1,
        Math.floor((Date.now() - new Date(data.memberSince).getTime()) / (1000 * 60 * 60 * 24))
      )
    : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-xl border border-white/[0.08] bg-gradient-to-br from-emerald-500/[0.08] to-transparent p-6">
        <div className="flex items-center gap-3 mb-2">
          <TrendingUp className="w-5 h-5 text-emerald-400" />
          <h2 className="text-white font-semibold text-base">{t("savings.heading")}</h2>
        </div>
        <p className="text-white/40 text-sm">
          {t("savings.subHeading")}
          {memberDays && <span> · {t("savings.memberFor", { count: memberDays })}</span>}
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4">
        <StatCard
          icon={<Clock className="w-4 h-4" />}
          label={t("savings.timeSaved")}
          value={`~${data.totalHoursSaved.toFixed(1)}h`}
          sub={
            memberDays
              ? `~${(data.totalHoursSaved / (memberDays / 30)).toFixed(1)}h / ${t("savings.perMonth")}`
              : undefined
          }
        />
        <StatCard
          icon={<Banknote className="w-4 h-4" />}
          label={t("savings.estimatedValue")}
          value={`~${data.totalMoneySaved.toLocaleString(numLocale)} ${t("savings.currency")}`}
          sub={t("savings.atRate", { rate: data.hourlyRate, currency: t("savings.currency") })}
        />
        <StatCard
          icon={<MessageSquare className="w-4 h-4" />}
          label={t("savings.conversationsLabel")}
          value={data.totalConversations.toLocaleString(numLocale)}
          sub={t("savings.messagesTotal", { count: data.totalMessages })}
        />
        <StatCard
          icon={<Zap className="w-4 h-4" />}
          label={t("savings.tokensUsed")}
          value={data.totalTokensUsed.toLocaleString(numLocale)}
        />
      </div>

      {/* Breakdown by module */}
      {data.modules.length > 0 && (
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-6">
          <h3 className="text-white font-semibold text-sm mb-4">{t("savings.byModule")}</h3>
          <div>
            {data.modules.map((mod) => (
              <ModuleRow key={mod.moduleId} mod={mod} />
            ))}
          </div>
        </div>
      )}

      {/* Methodology note */}
      <p className="text-white/20 text-xs">
        {t("savings.methodology", { rate: data.hourlyRate, currency: t("savings.currency") })}
      </p>
    </div>
  );
}
